import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import SwipeableCard from '../../../components/ui/SwipeableCard';
import { useDoubleTap } from '../../../hooks/useGestures';
import { supabase } from '../../../lib/supabase';
import { format, differenceInDays } from 'date-fns';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';

const BillCard = ({ bill, onEdit, onDelete, onMarkPaid, onSendReminder, isOverdue, isFavorite, onToggleFavorite }) => {
  const [toastMessage, setToastMessage] = useState(null);
  const [isArchived, setIsArchived] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const daysUntilDue = differenceInDays(new Date(bill?.next_due_date), new Date());

  const getCategoryIcon = (category) => {
    const icons = {
      utilities: 'Zap',
      transportation: 'Car',
      groceries: 'ShoppingCart',
      entertainment: 'Film',
      healthcare: 'Heart',
      dining: 'Utensils',
      shopping: 'ShoppingBag',
      housing: 'Home',
      insurance: 'Shield',
      education: 'BookOpen',
      other: 'MoreHorizontal'
    };
    return icons?.[category] || 'Receipt';
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      weekly: 'Weekly',
      biweekly: 'Bi-weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      yearly: 'Yearly'
    };
    return labels?.[frequency] || frequency;
  };

  const handleArchive = () => {
    setIsArchived(true);
    setToastMessage({
      type: 'info',
      message: `Bill archived: ${bill?.bill_name}`,
      action: () => {
        setIsArchived(false);
        setToastMessage(null);
      }
    });
    setTimeout(() => setToastMessage(null), 5000);
  };

  const handleSwipeDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete(bill?.id);
    setShowDeleteConfirm(false);
    setToastMessage(null);
  };

  const handleSwipeEdit = () => {
    onEdit(bill);
  };

  const handleToggleFavorite = async () => {
    const newFavoriteStatus = !isFavorite;

    // Optimistic update
    onToggleFavorite?.(bill?.id, newFavoriteStatus);

    try {
      const { error } = await supabase
        ?.from('recurring_bills')
        ?.update({ is_favorite: newFavoriteStatus })
        ?.eq('id', bill?.id);

      if (error) throw error;

      setToastMessage({
        type: 'info',
        message: newFavoriteStatus ? 'Added to favorites' : 'Removed from favorites'
      });
      setTimeout(() => setToastMessage(null), 2000);
    } catch (error) {
      console.error('Error updating favorite:', error);
      // Rollback on error
      onToggleFavorite?.(bill?.id, isFavorite);
      setToastMessage({
        type: 'warning',
        message: 'Failed to update favorite'
      });
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const doubleTapHandlers = useDoubleTap(handleToggleFavorite);

  return (
    <>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-20 right-4 z-50 max-w-sm">
          <div className={`flex items-center gap-3 p-4 rounded-lg border shadow-lg ${
            toastMessage?.type === 'warning' ? 'bg-warning/10 border-warning' : 'bg-primary/10 border-primary'
          }`}>
            <Icon name={toastMessage?.type === 'warning' ? 'AlertTriangle' : 'Info'} size={20} />
            <p className="text-sm font-medium flex-1">{toastMessage?.message}</p>
            <div className="flex gap-2">
              {toastMessage?.action && (
                <button
                  onClick={toastMessage?.action}
                  className="text-xs font-semibold px-3 py-1 rounded bg-primary text-white hover:bg-primary/90"
                >
                  {toastMessage?.type === 'warning' ? 'Confirm' : 'Undo'}
                </button>
              )}
              {toastMessage?.cancel && (
                <button
                  onClick={toastMessage?.cancel}
                  className="text-xs font-semibold px-3 py-1 rounded border hover:bg-muted"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <SwipeableCard
        onDelete={handleSwipeDelete}
        onArchive={handleArchive}
        onEdit={handleSwipeEdit}
        disabled={isArchived}
        className="rounded-xl"
      >
        <div className={`bg-card rounded-xl p-4 border shadow-sm hover:shadow-md transition-all ${
          isOverdue ? 'border-error/50 bg-error/5' : ''
        }`}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                isOverdue ? 'bg-error/10' : 'bg-primary/10'
              }`}>
                <Icon
                  name={getCategoryIcon(bill?.category)}
                  size={20}
                  color={isOverdue ? 'var(--color-error)' : 'var(--color-primary)'}
                  strokeWidth={2}
                />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-foreground">{bill?.bill_name}</h3>
                  <button
                    {...doubleTapHandlers}
                    className="focus:outline-none focus:ring-2 focus:ring-primary rounded p-1 transition-transform active:scale-90"
                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Icon
                      name="Star"
                      size={18}
                      color={isFavorite ? 'var(--color-warning)' : 'var(--color-muted-foreground)'}
                      fill={isFavorite ? 'var(--color-warning)' : 'none'}
                    />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground capitalize">{bill?.category}</p>
              </div>
            </div>
            {bill?.autopay_enabled && (
              <div className="flex items-center gap-1 px-2 py-1 bg-success/10 rounded-md">
                <Icon name="Check" size={12} color="var(--color-success)" />
                <span className="text-xs font-medium text-success">Auto</span>
              </div>
            )}
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Amount</span>
              <span className="text-lg font-bold text-foreground">${bill?.amount?.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Frequency</span>
              <span className="text-sm font-medium text-foreground">{getFrequencyLabel(bill?.frequency)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Next Due</span>
              <span className={`text-sm font-medium ${
                isOverdue ? 'text-error' : daysUntilDue <= 3 ? 'text-warning' : 'text-foreground'
              }`}>
                {format(new Date(bill?.next_due_date), 'MMM dd, yyyy')}
              </span>
            </div>
            {isOverdue ? (
              <div className="flex items-center gap-2 px-3 py-2 bg-error/10 rounded-lg">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                <span className="text-sm font-medium text-error">Overdue by {Math.abs(daysUntilDue)} days</span>
              </div>
            ) : daysUntilDue <= 7 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 rounded-lg">
                <Icon name="Clock" size={16} color="var(--color-warning)" />
                <span className="text-sm font-medium text-warning">Due in {daysUntilDue} days</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => onMarkPaid(bill)}
              variant="default"
              size="sm"
              iconName="Check"
              iconPosition="left"
              fullWidth
            >
              Mark Paid
            </Button>
            <Button
              onClick={() => onSendReminder(bill)}
              variant="outline"
              size="sm"
              iconName="Bell"
            />
            <Button
              onClick={() => onEdit(bill)}
              variant="outline"
              size="sm"
              iconName="Edit"
            />
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              variant="outline"
              size="sm"
              iconName="Trash2"
            />
          </div>
        </div>
      </SwipeableCard>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Bill"
        message="Are you sure you want to delete this recurring bill?"
        warningMessage="This will permanently remove the bill and all payment history. This action cannot be undone."
        itemDetails={{
          name: bill?.bill_name || 'N/A',
          amount: `$${parseFloat(bill?.amount)?.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
          frequency: bill?.frequency || 'N/A'
        }}
        confirmLabel="Delete Bill"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default BillCard;